import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { CredentialRequestDetailTemplate } from "../templates/CredentialRequestDetailTemplate";
import {
  useUserProfile,
  useCredentialRequest,
  useMinisterManagement,
  useRequestActions,
} from "../hooks";
import { getAllDistricts, getRegionsByDistrict } from "@/Services/regions.service";
import { getMinistersWithAvailability } from "@/Services/ministers.service";
import { getNextAvailableCodeNumber } from "@/Services/credentials.service";
import { updateRequest, submitRequest } from "@/data/credentialReqRepository";
import { CREDENTIAL_TYPES } from "@/models/credentialRequest";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { MinisterAvailability } from "../types";

/**
 * Page: Credential Request Detail
 * Inline dropdowns + unified minister grid
 */
export function CredentialRequestDetailPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Track admin edit mode for locked requests
  const [isAdminEditMode, setIsAdminEditMode] = useState(false);
  const [isSavingAdminChanges, setIsSavingAdminChanges] = useState(false);
  
  // Track notes and code fields
  const [notes, setNotes] = useState("");
  const [code, setCode] = useState("");
  const [isSavingCode, setIsSavingCode] = useState(false);
  const [suggestedCodeNumber, setSuggestedCodeNumber] = useState<number | undefined>(undefined);
  
  // Track dropdowns state
  const [districts, setDistricts] = useState<District[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState<string | null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  // For Function new requests, default year to current year immediately
  const [selectedYear, setSelectedYear] = useState<number | null>(() => {
    const path = window.location.pathname;
    const isNew = path.endsWith('/new');
    const isFunction = path.includes('/function');
    return isNew && isFunction ? new Date().getFullYear() : null;
  });
  
  // Track ministers state
  const [availableMinisters, setAvailableMinisters] = useState<MinisterAvailability[]>([]);
  const [isLoadingMinisters, setIsLoadingMinisters] = useState(false);
  const [selectedMinisterIds, setSelectedMinisterIds] = useState<Set<string>>(new Set());
  
  // Track pending changes for DRAFT/REJECTED (batch save)
  const [pendingAdds, setPendingAdds] = useState<Set<string>>(new Set());
  const [pendingRemoves, setPendingRemoves] = useState<Set<string>>(new Set());
  
  // Track original values for change detection
  const [originalNotes, setOriginalNotes] = useState("");
  const [originalCode, setOriginalCode] = useState("");
  const [originalMinisterIds, setOriginalMinisterIds] = useState<Set<string>>(new Set());

  // Get user profile and permissions
  const { userProfile, isAdmin, isLoading: isLoadingUser } = useUserProfile();

  // Load request data (hook internally uses useParams and useSearchParams)
  const {
    request,
    ministers,
    districtName,
    regionName,
    urlParams,
    isLoading: isLoadingRequest,
    canEdit,
    reloadRequest,
    isInstitutionRequest,
    isFunctionRequest,
    isNacionalFunctionRequest,
  } = useCredentialRequest();

  // Track if this is a new request
  const isNewRequest = !request;
  
  // Initialize notes and code when request loads
  useEffect(() => {
    if (request?.notes) {
      setNotes(request.notes);
      setOriginalNotes(request.notes);
    }
    if (request?.code) {
      setCode(request.code);
      setOriginalCode(request.code);
    } else {
      setCode("");
      setOriginalCode("");
    }
  }, [request]);
  
  // Auto-generate INSTITUTION code when request has no code yet
  useEffect(() => {
    if (isInstitutionRequest && selectedYear && suggestedCodeNumber !== undefined && !originalCode) {
      const yearSuffix = selectedYear.toString().slice(-2);
      const paddedNumber = String(suggestedCodeNumber).padStart(2, '0');
      setCode(`SOL${yearSuffix}-INST-${paddedNumber}`);
    }
  }, [isInstitutionRequest, selectedYear, suggestedCodeNumber, originalCode]);
  
  // Fetch suggested code number when request loads (for admin editable requests without code)
  // OR when creating new request with year selected
  useEffect(() => {
    async function fetchSuggestedCode() {
      const year = request?.year || selectedYear;
      const shouldFetch = 
        year && 
        (
          (request && !request.code && isAdmin) || 
          (!request && selectedYear)
        );
        
      if (shouldFetch) {
        try {
          const nextNumber = await getNextAvailableCodeNumber(year);
          setSuggestedCodeNumber(nextNumber);
        } catch (error) {
          console.error("Error fetching suggested code:", error);
        }
      }
    }
    fetchSuggestedCode();
  }, [request, isAdmin, selectedYear]);
  
  // Initialize dropdowns from existing request or URL params (run once)
  useEffect(() => {
    if (request) {
      // Existing request - use request data
      setSelectedDistrictId(request.district_id);
      setSelectedRegionId(request.region_id);
      setSelectedYear(request.year);
      
      // Initialize selected ministers from request
      if (ministers.length > 0) {
        const ministerIds = new Set(ministers.map(m => m.id));
        setSelectedMinisterIds(ministerIds);
        setOriginalMinisterIds(ministerIds);
        // Clear pending changes when request loads
        setPendingAdds(new Set());
        setPendingRemoves(new Set());
      }
    } else {
      // New request
      if (isInstitutionRequest || isFunctionRequest) {
        // For INSTITUTION/Function: Default year to current year
        setSelectedYear(new Date().getFullYear());
      } else if (urlParams.districtId || urlParams.regionId || urlParams.year) {
        // For MINISTERIAL: Use URL params if provided
        if (urlParams.districtId) setSelectedDistrictId(urlParams.districtId);
        if (urlParams.regionId) setSelectedRegionId(urlParams.regionId);
        if (urlParams.year) setSelectedYear(urlParams.year);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id, ministers.length]);
  
  // Load all districts on mount
  useEffect(() => {
    async function loadDistricts() {
      try {
        const data = await getAllDistricts();
        setDistricts(data);
      } catch (error) {
        console.error("Error loading districts:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los distritos",
          variant: "destructive",
        });
      }
    }
    loadDistricts();
  }, [toast]);

  // Load regions when district changes
  useEffect(() => {
    async function loadRegions() {
      if (!selectedDistrictId) {
        setRegions([]);
        return;
      }
      
      try {
        const data = await getRegionsByDistrict(selectedDistrictId);
        setRegions(data);
      } catch (error) {
        console.error("Error loading regions:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar las regiones",
          variant: "destructive",
        });
      }
    }
    loadRegions();
  }, [selectedDistrictId, toast]);

  // Load ministers when all 3 dropdowns are filled (or just year for INSTITUTION/Function)
  useEffect(() => {
    async function loadMinisters() {
      // INSTITUTION/DN only need year; MINISTERIAL and FUNCTION need district + region + year
      if (isInstitutionRequest || isNacionalFunctionRequest) {
        if (!selectedYear) {
          setAvailableMinisters([]);
          return;
        }
      } else {
        if (!selectedDistrictId || !selectedRegionId || !selectedYear) {
          setAvailableMinisters([]);
          return;
        }
      }

      setIsLoadingMinisters(true);
      try {
        const credentialType = isInstitutionRequest
          ? CREDENTIAL_TYPES.INSTITUTION
          : (isFunctionRequest || isNacionalFunctionRequest)
            ? CREDENTIAL_TYPES.FUNCTION
            : CREDENTIAL_TYPES.MINISTERIAL;

        const data = await getMinistersWithAvailability({
          districtId: (isInstitutionRequest || isNacionalFunctionRequest) ? undefined : (selectedDistrictId ?? undefined),
          regionId: (isInstitutionRequest || isNacionalFunctionRequest) ? undefined : (selectedRegionId ?? undefined),
          year: selectedYear!,
          credentialType: credentialType,
        });
        setAvailableMinisters(data);
      } catch (error) {
        console.error("Error loading ministers:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los ministros",
          variant: "destructive",
        });
      } finally {
        setIsLoadingMinisters(false);
      }
    }
    loadMinisters();
  }, [selectedDistrictId, selectedRegionId, selectedYear, toast, isInstitutionRequest, isFunctionRequest, isNacionalFunctionRequest]);

  // Minister management handlers
  const {
    isSubmitting: isSubmittingMinisters,
    addMultipleMinisters,
    removeMinister,
  } = useMinisterManagement({
    requestId: request?.id,
    isNewRequest: isNewRequest,
    districtId: selectedDistrictId,
    regionId: selectedRegionId,
    year: selectedYear || new Date().getFullYear(),
    userId: userProfile?.id,
    notes: notes,
    credentialType: isInstitutionRequest ? CREDENTIAL_TYPES.INSTITUTION : (isFunctionRequest ? CREDENTIAL_TYPES.FUNCTION : CREDENTIAL_TYPES.MINISTERIAL),
    code: code,
  });

  // Request action handlers
  const {
    isSubmitting: isSubmittingActions,
    handleSubmit,
    handleApprove,
    handleReject,
    handleMarkPrinted,
    handleMarkDelivered,
  } = useRequestActions({
    requestId: request?.id,
    userId: userProfile?.id,
    ministerCount: selectedMinisterIds.size,
    notes: notes,
    code: code,
    requestYear: request?.year || undefined,
    isAdmin: isAdmin,
    onRequestUpdate: () => {
      reloadRequest();
    },
    onCodeValidationError: (error) => {
      toast({
        variant: "destructive",
        title: "Código inválido",
        description: error,
      });
    },
    onSuccess: (action) => {
      // Show success toast and navigate back
      const messages = {
        submit: "Solicitud enviada correctamente",
        approve: "Solicitud aprobada correctamente",
        reject: "Solicitud observada correctamente",
        print: "Solicitud marcada como impresa",
        deliver: "Solicitud marcada como entregada",
      };
      
      toast({
        title: "Éxito",
        description: messages[action],
      });
      
      // Navigate back to previous page
      setTimeout(() => navigate(-1), 500);
    },
  });

  // Combined submission state
  const isSubmitting = isSubmittingMinisters || isSubmittingActions;
  
  // Calculate if there are unsaved changes
  const hasUnsavedChanges = 
    notes !== originalNotes ||
    code !== originalCode ||
    pendingAdds.size > 0 ||
    pendingRemoves.size > 0;
  
  // Calculate detailed pending changes for the dialog
  const pendingChanges = {
    notesChanged: notes !== originalNotes,
    codeChanged: code !== originalCode,
    ministersAdded: pendingAdds.size,
    ministersRemoved: pendingRemoves.size,
  };
  
  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && request && ['DRAFT', 'REJECTED'].includes(request.status || '')) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, request]);

  // Handle save (update notes, code, AND minister changes in batch)
  const handleSave = useCallback(async () => {
    if (!request?.id) return;
    
    setIsSavingCode(true);
    try {
      // Update notes and code (code format is guaranteed by UI)
      const updates: Record<string, string | null> = {
        notes: notes.trim() || null,
        code: code.trim() || null,
      };

      await updateRequest(request.id, updates);
      
      // Batch apply minister changes
      if (pendingAdds.size > 0) {
        await addMultipleMinisters(
          pendingAdds,
          availableMinisters,
          () => {} // No action needed, will reload after all changes
        );
      }
      
      // Remove ministers one by one (no batch remove function)
      for (const ministerId of pendingRemoves) {
        await removeMinister(ministerId, () => {});
      }
      
      // Clear pending changes
      setPendingAdds(new Set());
      setPendingRemoves(new Set());
      
      // Update original values to reflect the saved state
      setOriginalNotes(notes.trim() || "");
      setOriginalCode(code.trim() || "");
      // Calculate new original minister IDs after applying adds/removes
      const newMinisterIds = new Set(originalMinisterIds);
      pendingAdds.forEach(id => newMinisterIds.add(id));
      pendingRemoves.forEach(id => newMinisterIds.delete(id));
      setOriginalMinisterIds(newMinisterIds);
      
      toast({
        title: "Cambios guardados",
        description: "La solicitud fue actualizada correctamente",
      });
      
      // Reload once after all changes
      reloadRequest();
      
      // Navigate back after a short delay (keep loading state until navigation)
      setTimeout(() => {
        navigate(-1);
        setIsSavingCode(false);
      }, 500);
    } catch (error) {
      console.error("Error saving changes:", error);
      toast({
        title: "Error",
        description: "No se pudieron guardar los cambios",
        variant: "destructive",
      });
      setIsSavingCode(false);
    }
  }, [request?.id, notes, code, pendingAdds, pendingRemoves, originalMinisterIds, toast, reloadRequest, addMultipleMinisters, availableMinisters, removeMinister]);

  // Handle save for admin edit mode (works on all states)
  const handleAdminEditSave = useCallback(async () => {
    if (!request?.id || !isAdmin || !isAdminEditMode) return;
    
    setIsSavingAdminChanges(true);
    try {
      // Update notes and code
      const updates: Record<string, string | null> = {
        notes: notes.trim() || null,
        code: code.trim() || null,
      };

      await updateRequest(request.id, updates);
      
      // Batch apply minister changes
      if (pendingAdds.size > 0) {
        await addMultipleMinisters(
          pendingAdds,
          availableMinisters,
          () => {} // No action needed, will reload after all changes
        );
      }
      
      // Remove ministers one by one
      for (const ministerId of pendingRemoves) {
        await removeMinister(ministerId, () => {});
      }
      
      // Clear pending changes
      setPendingAdds(new Set());
      setPendingRemoves(new Set());
      
      // Update original values
      setOriginalNotes(notes.trim() || "");
      setOriginalCode(code.trim() || "");
      const newMinisterIds = new Set(originalMinisterIds);
      pendingAdds.forEach(id => newMinisterIds.add(id));
      pendingRemoves.forEach(id => newMinisterIds.delete(id));
      setOriginalMinisterIds(newMinisterIds);
      
      toast({
        title: "Cambios guardados (Admin)",
        description: "La solicitud fue actualizada correctamente por el administrador",
      });
      
      // Reload request to reflect changes
      await reloadRequest();
      
      // Keep loading state until navigation completes
      setTimeout(() => {
        navigate(-1);
        setIsSavingAdminChanges(false);
      }, 500);
    } catch (error) {
      console.error("Error saving admin changes:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudieron guardar los cambios",
        variant: "destructive",
      });
      setIsSavingAdminChanges(false);
    }
  }, [request?.id, isAdmin, isAdminEditMode, notes, code, pendingAdds, pendingRemoves, originalMinisterIds, toast, reloadRequest, addMultipleMinisters, availableMinisters, removeMinister]);

  // Dropdown change handlers
  const handleDistrictChange = (districtId: string) => {
    setSelectedDistrictId(districtId);
    setSelectedRegionId(null); // Reset region when district changes
    setAvailableMinisters([]); // Clear ministers
    setSelectedMinisterIds(new Set()); // Reset selection
    setPendingAdds(new Set()); // Clear pending changes
    setPendingRemoves(new Set());
  };

  const handleRegionChange = (regionId: string) => {
    setSelectedRegionId(regionId);
    setSelectedMinisterIds(new Set()); // Reset selection
    setPendingAdds(new Set()); // Clear pending changes
    setPendingRemoves(new Set());
    // Ministers will reload via useEffect
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setSelectedMinisterIds(new Set()); // Reset selection
    setPendingAdds(new Set()); // Clear pending changes
    setPendingRemoves(new Set());
    // Ministers will reload via useEffect
  };

  // Minister selection handler with confirmation and toast
  const handleMinisterSelectionChange = useCallback(async (ministerId: string, selected: boolean) => {
    const minister = availableMinisters.find(m => m.id === ministerId);
    if (!minister) return;
    
    // Update UI immediately (optimistic update)
    setSelectedMinisterIds(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(ministerId);
      } else {
        newSet.delete(ministerId);
      }
      return newSet;
    });
    
    // For DRAFT/REJECTED: Track changes for batch save (no immediate DB call)
    // Also track for admin edit mode on any status
    if (request && (['DRAFT', 'REJECTED'].includes(request.status || '') || (isAdmin && isAdminEditMode))) {
      const wasOriginallyInRequest = originalMinisterIds.has(ministerId);
      
      if (selected) {
        // Adding minister
        if (wasOriginallyInRequest) {
          // Was removed before, now adding back - cancel the remove
          setPendingRemoves(prev => {
            const newSet = new Set(prev);
            newSet.delete(ministerId);
            return newSet;
          });
        } else {
          // Brand new addition
          setPendingAdds(prev => new Set(prev).add(ministerId));
        }
      } else {
        // Removing minister
        if (wasOriginallyInRequest) {
          // Was in original request - mark for removal
          setPendingRemoves(prev => new Set(prev).add(ministerId));
        } else {
          // Was a pending add - just cancel the add
          setPendingAdds(prev => {
            const newSet = new Set(prev);
            newSet.delete(ministerId);
            return newSet;
          });
        }
      }
    } else if (request && !['DRAFT', 'REJECTED'].includes(request.status || '')) {
      // For other statuses without admin edit mode - do nothing (read-only)
    }
    // For new requests (no request object), changes are already tracked in selectedMinisterIds
  }, [availableMinisters, request, originalMinisterIds, isAdmin, isAdminEditMode]);

  // Handle submit for new requests (create request with selected ministers)
  const handleSubmitNewRequest = async () => {
    if (selectedMinisterIds.size === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un ministro",
        variant: "destructive",
      });
      return;
    }
    
    await addMultipleMinisters(
      selectedMinisterIds,
      availableMinisters,
      (addedMinisters) => {
        // Show success toast and navigate back
        toast({
          title: "Solicitud creada",
          description: `Se creó la solicitud con ${addedMinisters.length} ministro${addedMinisters.length !== 1 ? 's' : ''}`,
        });
        
        // Navigate back to the list
        setTimeout(() => navigate(-1), 500);
      }
    );
  };
  
  // Handle create and submit for new requests (with auto-code assignment)
  const handleCreateAndSubmit = async () => {
    if (selectedMinisterIds.size === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un ministro",
        variant: "destructive",
      });
      return;
    }
    
    // Auto-assign code if empty (only for Guardar y Enviar)
    let finalCode = code.trim();
    if (!finalCode && suggestedCodeNumber !== undefined && selectedYear) {
      const yearSuffix = selectedYear.toString().slice(-2);
      const paddedNumber = String(suggestedCodeNumber).padStart(3, '0');
      finalCode = `SOL${yearSuffix}-${paddedNumber}`;
      setCode(finalCode);
    }
    
    let newRequestId: string | undefined;
    
    // First create the request with ministers (suppress auto-navigation)
    await addMultipleMinisters(
      selectedMinisterIds,
      availableMinisters,
      async (_addedMinisters, createdRequestId) => {
        newRequestId = createdRequestId;
      },
      true // suppressNavigation
    );
    
    // Then submit it using the new request ID
    if (newRequestId) {
      try {
        // Update code if auto-assigned
        if (finalCode) {
          await updateRequest(newRequestId, { code: finalCode });
        }
        
        // Submit the request
        await submitRequest(newRequestId);
        
        toast({
          title: "Solicitud creada y enviada",
          description: `Se creó y envió la solicitud correctamente`,
        });
        
        // Navigate back to the list instead of to the new request detail page
        setTimeout(() => navigate(-1), 500);
      } catch (error) {
        console.error("Error submitting request:", error);
        toast({
          title: "Error",
          description: "La solicitud fue creada pero no se pudo enviar",
          variant: "destructive",
        });
        // Navigate back even on error
        setTimeout(() => navigate(-1), 500);
      }
    }
  };

  // Handle removing minister
  const handleRemoveMinister = async (ministerId: string) => {
    await removeMinister(ministerId, () => {
      // Update local state
      setSelectedMinisterIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(ministerId);
        return newSet;
      });
      reloadRequest();
    });
  };

  // Available years (current year and previous 2 years)
  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div className="container mx-auto py-6">
      <CredentialRequestDetailTemplate
        request={request}
        ministers={ministers}
        districtName={districtName}
        regionName={regionName}
        isNewRequest={!request}
        year={selectedYear || currentYear}
        isInstitutionRequest={isInstitutionRequest}
        isFunctionRequest={isFunctionRequest}
        isNacionalFunctionRequest={isNacionalFunctionRequest}
        districts={districts}
        regions={regions}
        availableYears={availableYears}
        selectedDistrictId={selectedDistrictId}
        selectedRegionId={selectedRegionId}
        selectedYear={selectedYear}
        onDistrictChange={handleDistrictChange}
        onRegionChange={handleRegionChange}
        onYearChange={handleYearChange}
        canEdit={canEdit}
        isAdmin={isAdmin}
        isLoadingRequest={isLoadingRequest || isLoadingUser}
        isLoadingMinisters={isLoadingMinisters}
        isSubmitting={isSubmitting}
        availableMinisters={availableMinisters}
        selectedMinisterIds={selectedMinisterIds}
        onMinisterSelectionChange={handleMinisterSelectionChange}
        onRemoveMinister={handleRemoveMinister}
        onSubmit={request ? handleSubmit : handleSubmitNewRequest}
        onSave={request && ['DRAFT', 'REJECTED'].includes(request.status || '') ? handleSave : undefined}
        onCreateAndSubmit={!request ? handleCreateAndSubmit : undefined}
        onApprove={handleApprove}
        onReject={handleReject}
        onMarkPrinted={handleMarkPrinted}
        onMarkDelivered={handleMarkDelivered}
        notes={notes}
        onNotesChange={setNotes}
        code={code}
        onCodeChange={setCode}
        onSaveCodeAndNotes={request && ['SUBMITTED', 'APPROVED'].includes(request.status || '') ? handleSave : undefined}
        isSavingCode={isSavingCode}
        hasUnsavedChanges={hasUnsavedChanges}
        pendingChanges={pendingChanges}
        suggestedCodeNumber={suggestedCodeNumber}
        isAdminEditMode={isAdminEditMode}
        onAdminEditModeChange={setIsAdminEditMode}
        onSaveAdminChanges={handleAdminEditSave}
        isSavingAdminChanges={isSavingAdminChanges}
      />
    </div>
  );
}
